// samplingEventForm/actions.js (aggregator)

import { makeCalibrationLogActions } from "./calibrationLogs/samplingEventFormCalibrationLogsActions";
import { makeDailySummaryActions } from "./dailySummaries/samplingEventFormDailySummariesActions";
import { makeFieldNotesActions } from "./fieldNotes/samplingEventFormFieldNotesActions";
import { makeInspectionsActions } from "./inspections/samplingEventFormInspectionsActions";
import { makeWellsActions } from "./wells/samplingEventFormWellsActions";
import { makeGroupsActions } from "./samplingGroups/samplingEventFormGroupsActions";
import { makeJsaFormsActions } from "./jsaForms/samplingEventFormJsaFormActions";
// Shared JSA action factories (UI/state only)
import { makeJsaBaseActions } from "../jsaForm/base/jsaFormBaseActions";
import { makeJsaCoreBasicInfoActions } from "../jsaForm/core/jsaFormCoreBasicInfoActions";
import { makeJsaCoreRacInfoActions } from "../jsaForm/core/jsaFormCoreRacInfoActions";
import { makeJsaCoreTrainingInfoActions } from "../jsaForm/core/jsaFormCoreTrainingActions";
import { makeJsaCoreSignatureInfoActions } from "../jsaForm/core/jsaFormCoreSignatureActions";
import { makeJsaV2HazardsActions } from "../jsaForm/v2/jsaFormV1HazardsActions";
import { makeJsaV2ControlMeasuresActions } from "../jsaForm/v2/jsaFormV1ControlMeasuresActions";

// Parent factory to assemble all section actions for samplingEventForm UI
// We mirror the DEL form pattern: provide a small api shim with shared helpers
export const makeSamplingEventFormActions = (set, get) => {
  const api = {
    // data getters/setters
    getCurrentEvent: () => get().activeSamplingEvent?.currentEvent || null,

    setCurrentEvent: (next) =>
      set((s) => ({
        activeSamplingEvent: { ...s.activeSamplingEvent, currentEvent: next },
      })),

    // Nested UI bucket helpers (mirrors activeDelDrafts.delForm)
    getFormUi: () => get().activeSamplingEvent?.samplingEventForm || { rev: 0 },
    setFormUi: (nextFormUi) =>
      set((s) => ({
        activeSamplingEvent: {
          ...s.activeSamplingEvent,
          samplingEventForm: nextFormUi,
        },
      })),

    // bump and UI setters
    bump: (patch = null) => {
      const prev = api.getFormUi();
      const nextRev = (prev.rev || 0) + 1;
      api.setFormUi(
        patch ? { ...prev, rev: nextRev, ...patch } : { ...prev, rev: nextRev }
      );
    },

    // Bump activeSamplingEvent._rev to trigger React re-render after model mutation
    bumpRevision: () =>
      set((s) => ({
        activeSamplingEvent: {
          ...s.activeSamplingEvent,
          _rev: (s.activeSamplingEvent._rev || 0) + 1,
        },
      })),

    setCalibrationLogUi: (patchOrFn) => {
      const prevForm = api.getFormUi();
      const prev = prevForm?.calibrationLogUI || {};
      const patch =
        typeof patchOrFn === "function" ? patchOrFn(prev) : patchOrFn;
      api.setFormUi({ ...prevForm, calibrationLogUI: { ...prev, ...patch } });
    },

    setDailySummaryUi: (patchOrFn) => {
      const prevForm = api.getFormUi();
      const prev = prevForm?.dailySummaryUI || {};
      const patch =
        typeof patchOrFn === "function" ? patchOrFn(prev) : patchOrFn;
      api.setFormUi({ ...prevForm, dailySummaryUI: { ...prev, ...patch } });
    },

    setFieldNotesUi: (patchOrFn) => {
      const prevForm = api.getFormUi();
      const prev = prevForm?.fieldNotesUI || {};
      const patch =
        typeof patchOrFn === "function" ? patchOrFn(prev) : patchOrFn;
      api.setFormUi({ ...prevForm, fieldNotesUI: { ...prev, ...patch } });
    },

    getFieldNotesUi: () => api.getFormUi()?.fieldNotesUI || {},

    setInspectionsUi: (patchOrFn) => {
      const prevForm = api.getFormUi();
      const prev = prevForm?.inspectionsUI || {};
      const patch =
        typeof patchOrFn === "function" ? patchOrFn(prev) : patchOrFn;
      api.setFormUi({ ...prevForm, inspectionsUI: { ...prev, ...patch } });
    },

    setWellsUi: (patchOrFn) => {
      const prevForm = api.getFormUi();
      const prev = prevForm?.wellsUI || {};
      const patch =
        typeof patchOrFn === "function" ? patchOrFn(prev) : patchOrFn;
      api.setFormUi({ ...prevForm, wellsUI: { ...prev, ...patch } });
    },

    getWellsUi: () => api.getFormUi()?.wellsUI || {},

    // JSA UI helpers
    setJsaUiCommon: (patchOrFn) => {
      const prevForm = api.getFormUi();
      const prevJsa = prevForm?.jsaUI || {};
      const prev = prevJsa?.jsaUICommon || {};
      const patch =
        typeof patchOrFn === "function" ? patchOrFn(prev) : patchOrFn;
      api.setFormUi({
        ...prevForm,
        jsaUI: { ...prevJsa, jsaUICommon: { ...prev, ...patch } },
      });
    },

    setJsaUiById: (jsaId, patchOrFn) => {
      const prevForm = api.getFormUi();
      const prevJsa = prevForm?.jsaUI || {};
      const all = prevJsa?.jsaUIById || {};
      const prev = all[jsaId] || {};
      const incoming =
        typeof patchOrFn === "function" ? patchOrFn(prev) : patchOrFn;
      // Support nested functional patches and deep merge of buckets (base/core/v1/v2)
      const next = Object.keys(incoming || {}).reduce(
        (acc, key) => {
          const val = incoming[key];
          if (typeof val === "function") {
            acc[key] = val(prev[key]);
          } else if (val && typeof val === "object" && !Array.isArray(val)) {
            acc[key] = { ...(prev[key] || {}), ...val };
          } else {
            acc[key] = val;
          }
          return acc;
        },
        { ...prev }
      );

      api.setFormUi({
        ...prevForm,
        jsaUI: {
          ...prevJsa,
          jsaUIById: { ...all, [jsaId]: next },
        },
      });
    },

    setActiveJsaId: (jsaId) => {
      const prevForm = api.getFormUi();
      const prevJsa = prevForm?.jsaUI || {};
      api.setFormUi({
        ...prevForm,
        jsaUI: { ...prevJsa, activeJsaId: jsaId },
      });
    },

    getJsaUiById: (jsaId) =>
      (api.getFormUi()?.jsaUI?.jsaUIById || {})[jsaId] || {},
  };

  // Helper utils
  const toSnakeCase = (str) =>
    str == null
      ? str
      : String(str).replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);

  const getActiveJsaId = () => api.getFormUi()?.jsaUI?.activeJsaId;

  // Bridge shared JSA action factories to this slice's UI location (per-JSA id)
  const jsaUiHelpers = {
    bump: (patch) => api.bump(patch),
    setJsaUi: (patch) => {
      const jsaId = getActiveJsaId();
      if (!jsaId) return;
      api.setJsaUiById(jsaId, patch);
    },
  };

  // Base shared actions (no autosave)
  const sharedJsaBase = makeJsaBaseActions(jsaUiHelpers);
  const sharedJsaCore = {
    ...makeJsaCoreBasicInfoActions(jsaUiHelpers),
    ...makeJsaCoreRacInfoActions(jsaUiHelpers),
    ...makeJsaCoreTrainingInfoActions(jsaUiHelpers),
    ...makeJsaCoreSignatureInfoActions(jsaUiHelpers),
  };
  const sharedJsaV2 = {
    ...makeJsaV2HazardsActions(jsaUiHelpers),
    ...makeJsaV2ControlMeasuresActions(jsaUiHelpers),
  };

  // Autosave wrappers using samplingEventForm/jsaForms actions
  const seJsaAutosave = makeJsaFormsActions(api);

  // Format date to YYYY-MM-DD (UTC) for API
  const formatDateYYYYMMDD = (date) => {
    if (!date) return null;
    try {
      const d = new Date(date);
      const y = d.getUTCFullYear();
      const m = String(d.getUTCMonth() + 1).padStart(2, "0");
      const dd = String(d.getUTCDate()).padStart(2, "0");
      return `${y}-${m}-${dd}`;
    } catch {
      return null;
    }
  };

  // Wrap a function to call autosave for basic_info
  const withBasicInfoAutosave =
    (fn, fieldNameOrResolver) =>
    async (...args) => {
      const result = await fn(...args);
      try {
        const jsaId = args[0]?.jsaBasicInfo?.id; // first arg is jsaData
        if (!jsaId) return result;
        const fieldSnake =
          typeof fieldNameOrResolver === "function"
            ? fieldNameOrResolver(...args)
            : fieldNameOrResolver;
        const valueResolver =
          typeof fieldNameOrResolver === "function"
            ? fieldNameOrResolver.value
            : null;
        const value = valueResolver ? valueResolver(...args) : args[1];
        await seJsaAutosave.updateBasicInfoField(jsaId, fieldSnake, value);
      } catch {}
      return result;
    };

  // Build core (basic info) wrappers explicitly
  const wrappedCore = {
    ...sharedJsaCore,
    updateJsaClientName: withBasicInfoAutosave(
      sharedJsaCore.updateJsaClientName,
      "jsa_client_name"
    ),

    updateJsaJobDescription: withBasicInfoAutosave(
      sharedJsaCore.updateJsaJobDescription,
      "jsa_job_description"
    ),

    selectJsaSupervisor: async (jsaData, supervisor) => {
      await sharedJsaCore.selectJsaSupervisor(jsaData, supervisor);
      try {
        const jsaId = jsaData?.jsaBasicInfo?.id;
        const payload = supervisor
          ? {
              supervisor_id: supervisor.value ?? null,
              supervisor_name: supervisor.label ?? null,
            }
          : { supervisor_id: null, supervisor_name: null };
        await seJsaAutosave.updateBasicInfoField(
          jsaId,
          "supervisor_id",
          payload
        );
      } catch {}
    },

    updateJsaCompletedTime: withBasicInfoAutosave(
      sharedJsaCore.updateJsaCompletedTime,
      "jsa_completed_time"
    ),

    updateJsaCompletedDate: async (jsaData, completedDate) => {
      await sharedJsaCore.updateJsaCompletedDate(jsaData, completedDate);
      try {
        const jsaId = jsaData?.jsaBasicInfo?.id;
        await seJsaAutosave.updateBasicInfoField(
          jsaId,
          "jsa_completed_date",
          formatDateYYYYMMDD(completedDate)
        );
      } catch {}
    },

    updateSiteLocation: async (jsaData, selected, googleSessionToken) => {
      await sharedJsaCore.updateSiteLocation(
        jsaData,
        selected,
        googleSessionToken
      );
      try {
        const jsaId = jsaData?.jsaBasicInfo?.id;
        const label = jsaData?.jsaBasicInfo?.siteLocation?.label || null;
        await seJsaAutosave.updateBasicInfoField(jsaId, "site_location", label);
        // Also send the medical facility fields that were populated by getMedicalFacility
        const medicalFacility = jsaData?.jsaBasicInfo?.medicalFacility || null;
        const medicalFacilityAddress =
          jsaData?.jsaBasicInfo?.medicalFacilityAddress || null;
        if (medicalFacility !== null) {
          await seJsaAutosave.updateBasicInfoField(
            jsaId,
            "medical_facility",
            medicalFacility
          );
        }
        if (medicalFacilityAddress !== null) {
          await seJsaAutosave.updateBasicInfoField(
            jsaId,
            "medical_facility_address",
            medicalFacilityAddress
          );
        }
      } catch {}
    },

    updateManualSiteLocation: async (jsaData, siteLocation) => {
      await sharedJsaCore.updateManualSiteLocation(jsaData, siteLocation);
      try {
        const jsaId = jsaData?.jsaBasicInfo?.id;
        const label = siteLocation || null;
        await seJsaAutosave.updateBasicInfoField(jsaId, "site_location", label);
      } catch {}
    },

    updateManualMedicalFacility: async (jsaData, medicalFacility) => {
      await sharedJsaCore.updateManualMedicalFacility(jsaData, medicalFacility);
      try {
        const jsaId = jsaData?.jsaBasicInfo?.id;
        await seJsaAutosave.updateBasicInfoField(
          jsaId,
          "medical_facility",
          medicalFacility
        );
      } catch {}
    },

    updateManualMedicalFacilityAddress: async (jsaData, address) => {
      await sharedJsaCore.updateManualMedicalFacilityAddress(jsaData, address);
      try {
        const jsaId = jsaData?.jsaBasicInfo?.id;
        await seJsaAutosave.updateBasicInfoField(
          jsaId,
          "medical_facility_address",
          address
        );
      } catch {}
    },

    // Signatures: save via autosave endpoint after updating model
    updateJsaSignatureEmployeeSignature: async (
      jsaData,
      signature,
      employeeRowId
    ) => {
      await sharedJsaCore.updateJsaSignatureEmployeeSignature(
        jsaData,
        signature,
        employeeRowId
      );
      try {
        const jsaId = jsaData?.jsaBasicInfo?.id;
        const row = jsaData?.jsaEmployeeSignaturesInfo?.find(
          (r) => r.rowId === employeeRowId
        );
        if (row) {
          await seJsaAutosave.saveSignatures(jsaId, row.toJson());
        }
      } catch {}
    },

    // Auto-add employees: only send NEW rows to server (preserve existing signatures)
    autoAddAllEmployeesToJsaEmployeeSignatureInfo: async (
      jsaData,
      employees
    ) => {
      // Capture existing row IDs before adding new employees
      const existingRowIds = new Set(
        (jsaData?.jsaEmployeeSignaturesInfo || []).map((r) => r.rowId)
      );

      // Call the shared action (adds new rows to the model, filters duplicates)
      await sharedJsaCore.autoAddAllEmployeesToJsaEmployeeSignatureInfo(
        jsaData,
        employees
      );

      // Find only the NEW rows that were just added
      const allRows = jsaData?.jsaEmployeeSignaturesInfo || [];
      const newRows = allRows.filter((r) => !existingRowIds.has(r.rowId));

      // Only send NEW rows to the server (preserve existing signatures)
      if (newRows.length > 0) {
        try {
          const jsaId = jsaData?.jsaBasicInfo?.id;
          const logs = newRows.map((row) => ({
            id: row.rowId,
            employee_id: row.employeeId || null,
            employee_sig: null,
          }));
          await seJsaAutosave.saveSignatures(jsaId, logs);
        } catch {}
      }
    },
  };

  // Wrap RAC actions → autosave with snake field derived from method name
  const racMethodToField = (method) =>
    toSnakeCase(method.replace(/^updateJsa/, ""));
  const wrappedRac = Object.fromEntries(
    Object.entries(sharedJsaCore)
      .filter(([k]) => k.startsWith("updateJsaRac"))
      .map(([k, fn]) => [
        k,
        async (jsaData, value) => {
          await fn(jsaData, value);
          try {
            const jsaId = jsaData?.jsaBasicInfo?.id;
            const field = racMethodToField(k);
            await seJsaAutosave.updateRacField(jsaId, field, value);
          } catch {}
        },
      ])
  );

  // Wrap V2 hazards/control measures
  const wrappedV2 = {
    ...sharedJsaV2,

    updateJsaV2Hazards: async (jsaData, hazardsType, value, otherCheckbox) => {
      await sharedJsaV2.updateJsaV2Hazards(
        jsaData,
        hazardsType,
        value,
        otherCheckbox
      );
      try {
        const jsaId = jsaData?.jsaBasicInfo?.id;
        await seJsaAutosave.updateHazardField(
          jsaId,
          toSnakeCase(hazardsType),
          value
        );
      } catch {}
    },

    updateJsaV2HazardsOtherDescribe: async (jsaData, value) => {
      await sharedJsaV2.updateJsaV2HazardsOtherDescribe(jsaData, value);
      try {
        const jsaId = jsaData?.jsaBasicInfo?.id;
        await seJsaAutosave.updateHazardField(jsaId, "other_describe", value);
      } catch {}
    },

    updateJsaV2Cm: async (jsaData, attribute, value) => {
      await sharedJsaV2.updateJsaV2Cm(jsaData, attribute, value);
      try {
        const jsaId = jsaData?.jsaBasicInfo?.id;
        await seJsaAutosave.updateControlMeasureField(
          jsaId,
          toSnakeCase(attribute),
          value
        );
      } catch {}
    },
  };

  const jsaShared = {
    base: sharedJsaBase,
    core: { ...wrappedCore, ...wrappedRac },
    v1: {}, // Not used for Sampling Event (V2 only)
    v2: wrappedV2,
  };

  // UI-only (no autosave) action set for shared components. These mutate the
  // local model and UI only; callers can use the commit bundle to persist.
  // Exceptions use wrapped versions that handle autosave internally:
  // - updateSiteLocation: sends medical_facility fields alongside site_location
  // - autoAddAllEmployeesToJsaEmployeeSignatureInfo: only sends NEW rows to server
  const jsaSharedUIOnly = {
    base: sharedJsaBase,
    core: {
      ...sharedJsaCore,
      updateSiteLocation: wrappedCore.updateSiteLocation,
      autoAddAllEmployeesToJsaEmployeeSignatureInfo:
        wrappedCore.autoAddAllEmployeesToJsaEmployeeSignatureInfo,
    },
    v1: {},
    v2: sharedJsaV2,
  };

  // Commit bundle used by shared components (e.g., via onBlur/onChange) to
  // persist changes over websocket/REST fallback through seJsaAutosave.
  const jsaCommit = {
    basicInfo: async (jsaData, fieldSnakeCase, value) => {
      try {
        const jsaId = jsaData?.jsaBasicInfo?.id;
        if (jsaId)
          await seJsaAutosave.updateBasicInfoField(
            jsaId,
            fieldSnakeCase,
            value
          );
      } catch {}
    },

    rac: async (jsaData, fieldSnakeCase, value) => {
      try {
        const jsaId = jsaData?.jsaBasicInfo?.id;
        if (jsaId)
          await seJsaAutosave.updateRacField(jsaId, fieldSnakeCase, value);
      } catch {}
    },

    hazard: async (jsaData, attributeSnakeCase, value) => {
      try {
        const jsaId = jsaData?.jsaBasicInfo?.id;
        if (jsaId)
          await seJsaAutosave.updateHazardField(
            jsaId,
            attributeSnakeCase,
            value
          );
      } catch {}
    },

    controlMeasure: async (jsaData, attributeSnakeCase, value) => {
      try {
        const jsaId = jsaData?.jsaBasicInfo?.id;
        if (jsaId)
          await seJsaAutosave.updateControlMeasureField(
            jsaId,
            attributeSnakeCase,
            value
          );
      } catch {}
    },

    signatures: async (jsaData, logs) => {
      try {
        const jsaId = jsaData?.jsaBasicInfo?.id;
        if (jsaId) await seJsaAutosave.saveSignatures(jsaId, logs);
      } catch {}
    },

    deleteSignature: async (jsaData, signatureId) => {
      try {
        const jsaId = jsaData?.jsaBasicInfo?.id;
        if (jsaId) await seJsaAutosave.deleteSignature(jsaId, signatureId);
      } catch {}
    },

    copy: async (srcJsaId, completedDateYYYYMMDD) => {
      try {
        await seJsaAutosave.copyJsa(srcJsaId, completedDateYYYYMMDD);
      } catch {}
    },

    deleteJsa: async (jsaId) => {
      try {
        await seJsaAutosave.deleteJsa(jsaId);
      } catch {}
    },
  };

  // Build section actions so we can reference them in handleBroadcast
  const calibrationLogs = makeCalibrationLogActions(api);
  const dailySummaries = makeDailySummaryActions(api);
  const fieldNotes = makeFieldNotesActions(api);
  const inspections = makeInspectionsActions(api);
  const wells = makeWellsActions(api);
  const groups = makeGroupsActions(api);
  const jsaForms = makeJsaFormsActions(api);

  /**
   * Helper: Convert snake_case key to camelCase
   */
  const toCamelCase = (str) =>
    str.replace(/_([a-z])/g, (_, c) => c.toUpperCase());

  /**
   * Helper: Apply patch fields to an object using setter methods or direct assignment
   * @param {object} target - The class instance to update
   * @param {object} patch - The patch data with snake_case keys
   * @param {array} skipKeys - Keys to skip (e.g., ['id', 'action'])
   */
  const applyPatchToObject = (target, patch, skipKeys = ["id", "action"]) => {
    Object.entries(patch).forEach(([key, value]) => {
      if (skipKeys.includes(key)) return;

      const camelKey = toCamelCase(key);
      const setterName = `set${camelKey
        .charAt(0)
        .toUpperCase()}${camelKey.slice(1)}`;

      if (typeof target[setterName] === "function") {
        target[setterName](value);
      } else if (camelKey in target) {
        target[camelKey] = value;
      }
    });
  };

  /**
   * Handle event-level field updates (completed, etc.)
   */
  const handleEventBroadcast = (patch) => {
    const event = api.getCurrentEvent();
    if (!event) return;

    applyPatchToObject(event, patch);
    api.bump();
  };

  /**
   * Central broadcast router - routes by patch_type to section handlers.
   * Each section handler mutates the class instance directly using setters,
   * then calls api.bump() to trigger re-render.
   * @param {object} patchData - { patch_type, patch, }
   */
  const handleBroadcast = (patchData) => {
    const { patch_type, patch } = patchData;
    console.log("handleBroadcast: patchData:", patch_type, patch);

    switch (patch_type) {
      case "sampling_event_daily_summary":
        if (dailySummaries.handleBroadcast) {
          dailySummaries.handleBroadcast(patch, applyPatchToObject);
        }
        break;

      case "water_calibration_log":
        if (calibrationLogs.handleBroadcast) {
          calibrationLogs.handleBroadcast(patch, applyPatchToObject);
        }
        break;

      case "sampling_event_field_notes":
        if (fieldNotes.handleBroadcast) {
          fieldNotes.handleBroadcast(patch, applyPatchToObject);
        }
        break;

      case "sampling_event_wells_inspection":
        if (inspections.handleBroadcast) {
          inspections.handleBroadcast(patch, applyPatchToObject);
        }
        break;

      case "sampling_event_wells_sampling":
        if (wells.handleBroadcast) {
          wells.handleBroadcast(patch, applyPatchToObject);
        }
        break;

      case "sampling_event_add_well":
        if (wells.handleAddWellBroadcast) {
          wells.handleAddWellBroadcast(patch);
        }
        break;

      case "sampling_group_employees":
        if (groups.handleBroadcast) {
          groups.handleBroadcast(patch);
        }
        break;

      case "sampling_event_field_measurements":
      case "sampling_event_sampling_employee_signatures":
        console.log(
          "handleBroadcast: sampling_event_field_measurements or sampling_event_sampling_employee_signatures"
        );
        // Pass parent_well_id for nested updates within a well
        if (wells.handleNestedBroadcast) {
          wells.handleNestedBroadcast(patch_type, patch, applyPatchToObject);
        }
        break;

      case "jsa_update":
      case "delete_jsa":
      case "copy_jsa":
      case "sampling_event_jsa_add":
      case "sampling_event_jsa_remove":
        if (jsaForms.handleBroadcast) {
          jsaForms.handleBroadcast(patchData, applyPatchToObject);
        }
        break;

      case "event_update":
        handleEventBroadcast(patch);
        break;

      default:
        console.warn("Unknown broadcast patch_type:", patch_type);
    }

    // Trigger React re-render by bumping revision
    api.bumpRevision();
  };

  return Object.freeze({
    calibrationLogs,
    dailySummaries,
    fieldNotes,
    inspections,
    wells,
    groups,
    jsaForms,
    jsaShared,
    jsaSharedUIOnly,
    jsaCommit,
    // Broadcast handler for incoming websocket updates
    handleBroadcast,
  });
};
